import { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, TrendingDown, TrendingUp, Bell, CheckCircle, XCircle } from 'lucide-react';
import { useProductBundles } from '@/hooks/useProductBundles';
import { useBundleConversionStats } from '@/hooks/useBundleConversion';
import { useBundleUsageStats } from '@/hooks/useBundleAnalytics';

interface PerformanceAlert {
  bundle_id: string;
  bundle_name: string;
  alert_type: 'underperforming' | 'declining' | 'zero_sales' | 'low_conversion';
  severity: 'warning' | 'critical';
  message: string;
  metric: string;
  current_value: number;
  threshold: number;
}

export function BundlePerformanceAlerts() {
  const { data: bundles } = useProductBundles();
  const { data: conversionStats } = useBundleConversionStats();
  const { data: usageStats } = useBundleUsageStats();

  const alerts = useMemo(() => {
    if (!bundles || !conversionStats || !usageStats) return [];

    const alertList: PerformanceAlert[] = [];

    // Calculate averages for comparison
    const avgConversionRate = conversionStats.length > 0
      ? conversionStats.reduce((sum, s) => sum + s.conversion_rate, 0) / conversionStats.length
      : 0;
    
    const avgRevenue = usageStats.length > 0
      ? usageStats.reduce((sum, s) => sum + s.total_revenue, 0) / usageStats.length
      : 0;

    bundles.forEach(bundle => {
      if (!bundle.is_active) return;

      const conversion = conversionStats.find(c => c.bundle_id === bundle.id);
      const usage = usageStats.find(u => u.bundle_id === bundle.id);

      // Zero sales alert
      if (!usage || usage.usage_count === 0) {
        alertList.push({
          bundle_id: bundle.id,
          bundle_name: bundle.name,
          alert_type: 'zero_sales',
          severity: 'warning',
          message: 'No sales recorded for this bundle',
          metric: 'Usage Count',
          current_value: 0,
          threshold: 1,
        });
        return;
      }

      // Low conversion rate alert
      if (conversion && conversion.total_deals >= 3) {
        const conversionThreshold = Math.max(avgConversionRate * 0.5, 20);
        if (conversion.conversion_rate < conversionThreshold) {
          alertList.push({
            bundle_id: bundle.id,
            bundle_name: bundle.name,
            alert_type: 'low_conversion',
            severity: conversion.conversion_rate < 10 ? 'critical' : 'warning',
            message: `Conversion rate ${conversion.conversion_rate.toFixed(0)}% is below ${conversionThreshold.toFixed(0)}% threshold`,
            metric: 'Conversion Rate',
            current_value: conversion.conversion_rate,
            threshold: conversionThreshold,
          });
        }
      }

      // Underperforming revenue alert
      if (usage && avgRevenue > 0) {
        const revenueThreshold = avgRevenue * 0.3;
        if (usage.total_revenue < revenueThreshold && usage.usage_count >= 2) {
          alertList.push({
            bundle_id: bundle.id,
            bundle_name: bundle.name,
            alert_type: 'underperforming',
            severity: usage.total_revenue < revenueThreshold * 0.5 ? 'critical' : 'warning',
            message: `Revenue $${usage.total_revenue.toLocaleString()} is significantly below average`,
            metric: 'Total Revenue',
            current_value: usage.total_revenue,
            threshold: revenueThreshold,
          });
        }
      }

      // Declining bundle (high usage but low conversion)
      if (conversion && usage && usage.usage_count >= 5 && conversion.conversion_rate < 30) {
        const isAlreadyAlerted = alertList.some(a => a.bundle_id === bundle.id);
        if (!isAlreadyAlerted) {
          alertList.push({
            bundle_id: bundle.id,
            bundle_name: bundle.name,
            alert_type: 'declining',
            severity: 'warning',
            message: `High usage (${usage.usage_count}) but low conversion (${conversion.conversion_rate.toFixed(0)}%)`,
            metric: 'Usage/Conversion Ratio',
            current_value: conversion.conversion_rate,
            threshold: 30,
          });
        }
      }
    });

    return alertList.sort((a, b) => {
      if (a.severity === 'critical' && b.severity !== 'critical') return -1;
      if (a.severity !== 'critical' && b.severity === 'critical') return 1;
      return 0;
    });
  }, [bundles, conversionStats, usageStats]);

  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const warningCount = alerts.filter(a => a.severity === 'warning').length;

  const getAlertIcon = (type: PerformanceAlert['alert_type']) => {
    switch (type) {
      case 'zero_sales': return <XCircle className="h-4 w-4" />;
      case 'low_conversion': return <TrendingDown className="h-4 w-4" />;
      case 'underperforming': return <AlertTriangle className="h-4 w-4" />;
      case 'declining': return <TrendingDown className="h-4 w-4" />;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            <CardTitle>Bundle Performance Alerts</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {criticalCount > 0 && (
              <Badge variant="destructive">{criticalCount} Critical</Badge>
            )}
            {warningCount > 0 && (
              <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-600">
                {warningCount} Warning
              </Badge>
            )}
            {alerts.length === 0 && (
              <Badge variant="secondary" className="bg-green-500/20 text-green-600">
                <CheckCircle className="h-3 w-3 mr-1" />
                All Clear
              </Badge>
            )}
          </div>
        </div>
        <CardDescription>
          Automated alerts when bundles underperform their historical averages
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {alerts.length > 0 ? (
          <div className="space-y-3">
            {alerts.map((alert, index) => (
              <div 
                key={`${alert.bundle_id}-${alert.alert_type}`}
                className={`p-4 rounded-lg border ${
                  alert.severity === 'critical' 
                    ? 'bg-destructive/10 border-destructive/30' 
                    : 'bg-yellow-500/10 border-yellow-500/30'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className={alert.severity === 'critical' ? 'text-destructive' : 'text-yellow-600'}>
                        {getAlertIcon(alert.alert_type)}
                      </span>
                      <span className="font-semibold">{alert.bundle_name}</span>
                      <Badge 
                        variant={alert.severity === 'critical' ? 'destructive' : 'secondary'}
                        className={alert.severity === 'warning' ? 'bg-yellow-500/20 text-yellow-600' : ''}
                      >
                        {alert.alert_type.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{alert.message}</p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
                      <span>
                        {alert.metric}: <strong>{alert.current_value.toFixed(alert.metric.includes('Rate') ? 1 : 0)}{alert.metric.includes('Rate') ? '%' : ''}</strong>
                      </span>
                      <span>
                        Threshold: {alert.threshold.toFixed(alert.metric.includes('Rate') ? 1 : 0)}{alert.metric.includes('Rate') ? '%' : ''}
                      </span>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm">
                    View Bundle
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500 opacity-50" />
            <p className="font-medium">All bundles performing within expected ranges</p>
            <p className="text-sm">You'll be alerted when performance drops</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
